export type SurveyColorSchemeSetting = "Light" | "Dark" | "LightAndDark";
export type ResolvedColorScheme = "Light" | "Dark";

export function getSurveyColorScheme(
    supported: SurveyColorSchemeSetting | null | undefined,
    prefersDark: boolean
): ResolvedColorScheme {
    // Unset means Light, NOT LightAndDark as at the project level: an existing
    // survey has no setting and must not flip to dark on a dark device.
    const setting = supported || "Light";
    if (setting === "LightAndDark") {
        return prefersDark ? "Dark" : "Light";
    }
    return setting;
}

function prefersDark(): boolean {
    return !!window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

// One active stamp per element: when a new flow claims an element the previous
// stamp's media listener is retired, so a finished survey cannot keep re-applying
// its scheme under the flow that took over the page.
const activeStamps = new WeakMap<HTMLElement, () => void>();

// Stamp the survey's resolved scheme on an element: the modal for in-app surveys,
// <body> on standalone pages that own it. Returns a cleanup for when the survey ends.
export function stampSurveyColorScheme(element: HTMLElement, supported: SurveyColorSchemeSetting | null | undefined): () => void {
    activeStamps.get(element)?.();
    const apply = () => {
        const scheme = getSurveyColorScheme(supported, prefersDark());
        element.classList.toggle("survey-scheme-dark", scheme === "Dark");
        element.classList.toggle("survey-scheme-light", scheme === "Light");
    };
    apply();

    let query: MediaQueryList | null = null;
    if ((supported || "Light") === "LightAndDark" && window.matchMedia) {
        query = window.matchMedia("(prefers-color-scheme: dark)");
        query.addEventListener("change", apply);
    }
    const cleanup = () => {
        query?.removeEventListener("change", apply);
        element.classList.remove("survey-scheme-dark", "survey-scheme-light");
        if (activeStamps.get(element) === cleanup) {
            activeStamps.delete(element);
        }
    };
    activeStamps.set(element, cleanup);
    return cleanup;
}
